import * as t from 'io-ts';

import {DeepReadonly} from '../../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {
  CellsConversationsRange,
  CellsMessagesRange,
  CellsRange,
  RCellsConversationsRange,
  RCellsMessagesRange,
} from '../../../../helpers/cellsHelpers';
import {CellsContext, RCellsContext} from '../../../panels/cells/context/cellsContext';
import {
  InboxesConversationsParameters,
  InboxesParameters,
  InboxesParametersTypesEnum,
  InboxesTeammateMessagesParameters,
  RInboxesConversationsParameters,
  RInboxesTeammateMessagesParameters,
} from '../../../panels/inboxes/parameters/inboxesParameters';

/*
 * Runtime type.
 */

export const RCellsSyncContext = t.type({
  teammateId: t.number,
});

const RCellsListRangeBase = t.intersection([
  t.type({
    // The time at which we initially fetched the items.
    fetchedAt: t.number,
  }),
  t.partial({
    // The time at which we incrementally synced the items.
    syncedAt: t.union([t.number, t.undefined]),
  }),
]);

export enum CellsListTypesEnum {
  CONVERSATIONS = 'CONVERSATIONS',
  MESSAGES = 'MESSAGES',
}

const RCellsListStateBase = t.type({
  uid: t.string,
  cellsSyncContext: RCellsSyncContext,
  cellsContext: RCellsContext,
});

const RCellsConversationsListRange = t.intersection([
  // The bounds of this range.
  RCellsConversationsRange,
  t.type({
    // The unordered set of conversation IDs that were initially fetched.
    ids: t.readonlyArray(t.number),
  }),
  RCellsListRangeBase,
]);

const RCellsConversationsListState = t.intersection([
  RCellsListStateBase,
  t.type({
    type: t.literal(CellsListTypesEnum.CONVERSATIONS),
    parameters: RInboxesConversationsParameters,
    ranges: t.readonlyArray(RCellsConversationsListRange),
  }),
]);

const RCellsMessagesListRange = t.intersection([
  // The bounds of this range.
  RCellsMessagesRange,
  t.type({
    // The unordered set of message UIs that were fetched.
    ids: t.readonlyArray(t.string),
  }),
  RCellsListRangeBase,
]);

const RCellsMessagesListState = t.intersection([
  RCellsListStateBase,
  t.type({
    type: t.literal(CellsListTypesEnum.MESSAGES),
    parameters: RInboxesTeammateMessagesParameters,
    ranges: t.readonlyArray(RCellsMessagesListRange),
  }),
]);

export const RCellsListState = t.taggedUnion(
  'type',
  [RCellsConversationsListState, RCellsMessagesListState],
  'cellsList',
);

/*
 * Type.
 */

export type CellsIdForParameters<T extends InboxesParameters> = T extends InboxesTeammateMessagesParameters
  ? string
  : number;

export interface CellsSyncContext extends DeepReadonly<t.TypeOf<typeof RCellsSyncContext>> {}

export interface CellsListRange<TId> extends CellsRange<TId> {
  readonly ids: ReadonlyArray<TId>;
  readonly fetchedAt: number;
  readonly syncedAt?: number;
}

export interface CellsConversationsListRange
  extends CellsListRange<number>,
    CellsConversationsRange,
    DeepReadonly<t.TypeOf<typeof RCellsConversationsListRange>> {}

export interface CellsMessagesListRange
  extends CellsListRange<string>,
    CellsMessagesRange,
    DeepReadonly<t.TypeOf<typeof RCellsMessagesListRange>> {}

type CellsListTypeForParameters<T extends InboxesParameters> = T extends InboxesTeammateMessagesParameters
  ? CellsListTypesEnum.MESSAGES
  : CellsListTypesEnum.CONVERSATIONS;

export interface CellsListState<T extends InboxesParameters = InboxesParameters> {
  readonly uid: string;
  readonly cellsSyncContext: CellsSyncContext;
  readonly cellsContext: CellsContext;
  // This is redundant with the parameters type, but helps with the tagged union runtime type.
  readonly type: CellsListTypeForParameters<T>;
  readonly parameters: T;
  readonly ranges: ReadonlyArray<CellsListRange<CellsIdForParameters<T>>>;
}

// There is some redundancy here to sanity check all our types line up.
export interface CellsConversationsListState
  extends CellsListState<InboxesConversationsParameters>,
    DeepReadonly<t.TypeOf<typeof RCellsConversationsListState>> {
  readonly type: CellsListTypesEnum.CONVERSATIONS;
  readonly ranges: ReadonlyArray<CellsConversationsListRange>;
}

export interface CellsMessagesListState
  extends CellsListState<InboxesTeammateMessagesParameters>,
    DeepReadonly<t.TypeOf<typeof RCellsMessagesListState>> {
  readonly type: CellsListTypesEnum.MESSAGES;
  readonly ranges: ReadonlyArray<CellsMessagesListRange>;
}

/*
 * Helpers.
 */

export function cellsListTypeForParameters<T extends InboxesParameters>(
  parameters: T,
): CellsListTypeForParameters<T> {
  // TODO: Figure out why TS doesn't like this.
  // @ts-expect-error
  return parameters.type === InboxesParametersTypesEnum.TEAMMATE_MESSAGES
    ? CellsListTypesEnum.MESSAGES
    : CellsListTypesEnum.CONVERSATIONS;
}

export function makeCellsListTypePredicateForParameters<T extends InboxesParameters>(parameters: T) {
  const cellsListType = cellsListTypeForParameters(parameters);

  return (list: CellsListState | undefined): list is CellsListState<T> =>
    Boolean(list && list.type === cellsListType);
}

export function isCellsConversationsList(
  list: CellsListState | undefined,
): list is CellsConversationsListState {
  return Boolean(list && list.type === CellsListTypesEnum.CONVERSATIONS);
}
